import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";

import { getCookie, Logger } from "@/utils";

import { useEditorState } from "@/store";
import {
  notificationsList,
  notificationsMarkAsRead,
  notificationsUnreadCount,
} from "../api";

interface NotificationContextType {
  notifications: Notification[];
  newNotificationTrigger: number;
  refreshNotifications: () => Promise<void>;
  markAsReadAll: () => Promise<void>;
  markAsReadAllUIManipulation: () => void;
  unreadNotificationCount: number;
  loadMoreNotifications: () => Promise<void>;
  fetchingLoadMore: boolean;
}

interface MessageNotification {
  id: number;
  action: {
    type: string;
    name: string;
    action: string;
  }[];
  message: {
    tr: string;
    en: string;
  };
  templace_code: string;
  title: {
    tr: string;
    en: string;
  };
}

export interface Notification {
  /**
   *
   * @type {number}
   * @memberof Notification
   */
  id: number;
  /**
   *
   * @type {boolean}
   * @memberof Notification
   */
  is_read: boolean;
  /**
   *
   * @type {Date}
   * @memberof Notification
   */
  create_time: Date;
  /**
   *
   * @type {Array<NotificationAction>}
   * @memberof Notification
   */
  actions?: Array<NotificationAction>;
  /**
   *
   * @type {string}
   * @memberof Notification
   */
  title?: string;
  /**
   *
   * @type {string}
   * @memberof Notification
   */
  message?: string;
}

export interface NotificationAction {
  /**
   *
   * @type {string}
   * @memberof NotificationAction
   */
  type: string;
  /**
   *
   * @type {string}
   * @memberof NotificationAction
   */
  name: string;
  /**
   *
   * @type {string}
   * @memberof NotificationAction
   */
  action: string;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { tokens: { express_api_token: accessToken } = {}, project, language, initialized } = useEditorState();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [newNotificationTrigger, setNewNotificationTrigger] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const [lastFetchedNotificationPage, setLastFetchedNotificationPage] =
    useState(1);
  const [noLoadMore, setNoLoadMore] = useState(false);
  const [fetchingLoadMore, setFetchingLoadMore] = useState(false);

  const fetchNotifications = async () => {
    try {
      const data = await notificationsList(accessToken || "", 1, 5, language);
      Logger.log("setting notifications", data.results);
      setNotifications(data.results);
    } catch {
      /* empty */
    }
  };

  const fetchUnreadNotificationsCount = async () => {
    try {
      Logger.log("fetchUnreadNotificationsCount");
      const unreadNotificationCount = await notificationsUnreadCount(
        accessToken || ""
      );
      setUnreadNotificationCount((unreadNotificationCount as any).unread_count);
    } catch {
      /* empty */
    }
  };

  const loadMoreNotifications = async () => {
    if (noLoadMore || fetchingLoadMore) return;
    let notificationIds: Array<number> = [];
    try {
      setFetchingLoadMore(true);

      // const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
      // await sleep(3000); // 1 saniye bekler

      const data = await notificationsList(
        accessToken || "",
        lastFetchedNotificationPage + 1,
        5,
        language
      );
      setLastFetchedNotificationPage((prev) => prev + 1);
      setNotifications((prev) => {
        return [
          ...prev,
          ...data.results.filter(
            (notification: any) => !prev.find((n) => n.id === notification.id)
          ),
        ];
      });
      notificationIds = data.results.map(
        (notification: any) => notification.id
      );
    } catch {
      setNoLoadMore(true);
    }
    if (notificationIds.length > 0) {
      await markAsReadMany(notificationIds);
    }
    setFetchingLoadMore(false);
  };

  //const encryptor = createEncryptor("simpleKeyForSimpleEncryptor");

  const encryptMessage = useCallback((message: any) => {
    if (typeof message === "string") {
      //return encryptor.encrypt(message);
      return message; // For now, return as is
    } else if (typeof message === "object") {
      //return encryptor.encrypt(JSON.stringify(message));
      return JSON.stringify(message); // For now, return as is
    }
    return "ERROR";
  }, []);

  const markAsReadAll = async () => {
    const unreadedNotifications = notifications?.filter(
      (notification) => !notification.is_read
    );
    if (unreadedNotifications?.length === 0) return;
    try {
      await notificationsMarkAsRead(
        accessToken || "",
        encryptMessage(
          unreadedNotifications?.map((notification) => notification.id)
        )
      );
    } catch {
      /* empty */
    }
  };

  const markAsReadAllUIManipulation = () => {
    setUnreadNotificationCount(
      (prev) =>
        prev -
        notifications?.filter((notification) => !notification.is_read).length
    );
    setNotifications((prev) => {
      return prev?.map((notification) => {
        return {
          ...notification,
          is_read: true,
        };
      });
    });
  };

  const markAsRead = async (notificationId: number) => {
    try {
      await notificationsMarkAsRead(
        accessToken || "",
        encryptMessage([notificationId])
      );
    } catch {
      /* empty */
    }
  };

  const markAsReadMany = async (notificationIds: number[]) => {
    try {
      await notificationsMarkAsRead(
        accessToken || "",
        encryptMessage(notificationIds)
      );
    } catch {
      /* empty */
    }
  };

  const getWebSocketURL = (session_id: string) => {
    const host = window.location.hostname;

    let apiHost: string;
    if (host === "localhost" || host === "editor-staging.riders.ai") {
      apiHost = "api-staging.riders.ai";
    } else if (host === "editor.riders.ai") {
      apiHost = "api.riders.ai";
    } else {
      console.warn("Bilinmeyen host, varsayılan olarak staging kullanılacak");
      apiHost = "api-staging.riders.ai";
    }

    return `wss://${apiHost}/ws/notification/?sessionid=${session_id}`;
  };

  // Initial fetch effect
  useEffect(() => {
    if (!accessToken || !initialized || !project) return;
    fetchNotifications();
    fetchUnreadNotificationsCount();
  }, [accessToken, project, initialized]);

  const retryCount = useRef(0);
  const retryTimeout = useRef<NodeJS.Timeout | null>(null);

  // WebSocket effect
  useEffect(() => {
    if (!initialized || !project) return;

    let ws: WebSocket | null = null;

    const connect = () => {
      const isStaging = window.location.hostname.includes("staging");
      const csrfToken =
        getCookie(isStaging ? "stagingsessionid" : "sessionid") || "";
      Logger.log(getWebSocketURL(csrfToken || ""));

      if (!csrfToken) return;
      ws = new WebSocket(getWebSocketURL(csrfToken));

      ws.onopen = () => {
        retryCount.current = 0; // Bağlantı kurulduğunda retry sayacını sıfırla
      };

      ws.onmessage = (message) => {
        setNewNotificationTrigger((prev) => prev + 1);
        const messageNotification = JSON.parse(
          message.data
        ) as MessageNotification;
        setNotifications((prev) => {
          if (
            prev.find(
              (notification) => notification.id === messageNotification.id
            )
          ) {
            return prev;
          }
          return [
            {
              id: messageNotification.id,
              title: messageNotification.title[language],
              message: messageNotification.message[language],
              create_time: new Date(),
              is_read: false,
            },
            ...prev,
          ];
        });
        setUnreadNotificationCount((prev) => prev + 1);
        if (document.hasFocus()) {
          markAsRead(messageNotification.id).then();
        }
      };

      ws.onclose = () => {
        if (retryCount.current < 3) {
          retryCount.current += 1;
          retryTimeout.current = setTimeout(() => {
            connect();
          }, 3000); // 3 saniye sonra tekrar dene
        }
      };
    };

    connect();

    // Cleanup
    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
    };
  }, [project, initialized]); // accessToken çıkarıldı

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        refreshNotifications: fetchNotifications,
        newNotificationTrigger,
        markAsReadAll,
        markAsReadAllUIManipulation,
        unreadNotificationCount,
        loadMoreNotifications,
        fetchingLoadMore,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
